/**
 * Paramètres nommés des DTS officielles (CNSS, CNAMGS) : décisions du dirigeant en attente.
 *
 * Ces deux réglages sont volontairement isolés ici pour être changés en UNE ligne dès que la
 * décision est connue (aucune autre modification de code ni de gabarit n'est nécessaire).
 *
 * --- DEC-15 : ristourne « allocations familiales » (DTS CNSS) --------------------------------
 *   'A' (DÉFAUT, prudent) : AUCUNE déduction. Cotisation nette = cotisation brute, comme la
 *       feuille « DTS » du gabarit CNSS. L'Avis garde le libellé « RISTOURNE AF (2,00%) » avec D27 = 0.
 *   'B' : ristourne de `tauxRistourne` (2 %) appliquée à la masse salariale plafonnée CNSS.
 *   'C' : déduction du montant réel des allocations familiales versées (saisi par l'entreprise,
 *       `montantAllocationsFamilialesSaisi`).
 *
 * --- DEC-03 : plafond CNAMGS par salarié et par mois ----------------------------------------
 *   'A' (DÉFAUT) : assiette plafonnée à 2 500 000 par salarié et par mois, comme le moteur de
 *       paie (le gabarit affiche ce plafond mais ses formules ne l'appliquent pas).
 *   'B' : assiette NON plafonnée (si la CNAMGS le confirme). Approximation : le bulletin ne
 *       conserve que l'assiette déjà plafonnée ; au-dessus du plafond, l'assiette retenue est
 *       le brut imposable du bulletin (les indemnités exclues de la base CNAMGS ne peuvent pas
 *       être retrouvées depuis le bulletin : à affiner avec le moteur si B est retenue).
 */

export type OptionRistourneAf = 'A' | 'B' | 'C';
export type OptionPlafondCnamgs = 'A' | 'B';

export interface ParametresDts {
  readonly ristourneAf: {
    readonly option: OptionRistourneAf;
    /** Taux de la ristourne (option B), libellé du gabarit : « RISTOURNE AF (2,00%) ». */
    readonly tauxRistourne: number;
    /** Montant des allocations familiales versées sur le trimestre (option C), saisi par l'entreprise. */
    readonly montantAllocationsFamilialesSaisi: number;
  };
  readonly plafondCnamgs: {
    readonly option: OptionPlafondCnamgs;
  };
}

export const PARAMETRES_DTS_PAR_DEFAUT: ParametresDts = {
  ristourneAf: { option: 'A', tauxRistourne: 0.02, montantAllocationsFamilialesSaisi: 0 },
  plafondCnamgs: { option: 'A' },
};

export interface DeductionAf {
  /** Avis D26 : montant des allocations familiales (option C). */
  allocations_familiales: number;
  /** Avis D27 : ristourne AF (option B). */
  ristourne_af: number;
  /** Avis D28 = D26 + D27 : total déduit de la cotisation brute. */
  deduction_af: number;
}

/** Calcule la déduction AF selon l'option retenue. `masseCnss` = masse salariale plafonnée CNSS. */
export function calculerDeductionAf(
  masseCnss: number,
  parametres: ParametresDts['ristourneAf'],
): DeductionAf {
  switch (parametres.option) {
    case 'B': {
      const ristourne = Math.round(Math.max(0, masseCnss) * parametres.tauxRistourne);
      return { allocations_familiales: 0, ristourne_af: ristourne, deduction_af: ristourne };
    }
    case 'C': {
      const montant = Math.max(0, Math.round(parametres.montantAllocationsFamilialesSaisi));
      return { allocations_familiales: montant, ristourne_af: 0, deduction_af: montant };
    }
    default:
      return { allocations_familiales: 0, ristourne_af: 0, deduction_af: 0 };
  }
}

export class ParametresDtsInvalideError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ParametresDtsInvalideError';
  }
}

/** Réglages saisis par l'entreprise (tout est facultatif : le reste prend les décisions par défaut). */
export interface SaisieParametresDts {
  ristourneAf?: { option?: string; montantAllocationsFamilialesSaisi?: number };
  plafondCnamgs?: { option?: string };
}

/**
 * Valide et complète les réglages DTS. Le taux de ristourne (2 %) reste celui du code : seule l'option
 * et, pour l'option C, le montant des allocations familiales sont choisis par l'entreprise.
 */
export function resoudreParametresDts(saisie?: SaisieParametresDts | null): ParametresDts {
  const optionAf = saisie?.ristourneAf?.option ?? PARAMETRES_DTS_PAR_DEFAUT.ristourneAf.option;
  if (optionAf !== 'A' && optionAf !== 'B' && optionAf !== 'C') {
    throw new ParametresDtsInvalideError('Choix de ristourne invalide : choisissez l’une des trois propositions.');
  }
  const optionPlafond = saisie?.plafondCnamgs?.option ?? PARAMETRES_DTS_PAR_DEFAUT.plafondCnamgs.option;
  if (optionPlafond !== 'A' && optionPlafond !== 'B') {
    throw new ParametresDtsInvalideError('Choix de plafond CNAMGS invalide : choisissez l’une des deux propositions.');
  }
  const montant = saisie?.ristourneAf?.montantAllocationsFamilialesSaisi ?? PARAMETRES_DTS_PAR_DEFAUT.ristourneAf.montantAllocationsFamilialesSaisi;
  if (typeof montant !== 'number' || !Number.isFinite(montant) || montant < 0) {
    throw new ParametresDtsInvalideError('Le montant des allocations familiales doit être un nombre positif ou nul.');
  }
  if (optionAf === 'C' && montant <= 0) {
    throw new ParametresDtsInvalideError('Pour déduire le montant réel des allocations familiales, saisissez un montant supérieur à zéro.');
  }
  return {
    ristourneAf: {
      option: optionAf,
      tauxRistourne: PARAMETRES_DTS_PAR_DEFAUT.ristourneAf.tauxRistourne,
      montantAllocationsFamilialesSaisi: Math.round(montant),
    },
    plafondCnamgs: { option: optionPlafond },
  };
}
